/*
  Customer Reviews Gallery - shared lightbox viewer.

  Extracted from `review-gallery.js` so that every presentation of the review
  data - the homepage marquee, the product-page grid, anything added later -
  mounts the same lightbox rather than re-implementing it.

  This element knows nothing about marquees, grids or carousels. Its only
  contract is that it contains `button.m-review-gallery__media` elements
  carrying `data-index` and `data-full-*`, plus an optional
  `<script type="application/json" data-rg-strings>` block. Whatever wraps it
  can react to the two events it emits:

    rg:viewer-open   - the lightbox is being opened (bubbles)
    rg:viewer-close  - the lightbox has closed (bubbles)

  The `if (!customElements.get(...))` guard is required: `theme-editor.js`
  re-injects section `<script src>` tags on every `shopify:section:load`.
*/
if (!customElements.get('m-review-viewer')) {
  /** Memoised loader so the lightbox assets are fetched at most once per page. */
  let lightboxAssets = null;
  /** Single lightbox instance shared by every viewer on the page. */
  let lightboxElement = null;

  /**
   * Appends a resource tag and resolves once it has loaded.
   * @param {'script'|'link'} tagName
   * @param {string} url
   * @returns {Promise<void>}
   */
  function loadResource(tagName, url) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`[data-review-gallery-asset="${url}"]`);
      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
        } else {
          existing.addEventListener('load', () => resolve(), { once: true });
          existing.addEventListener('error', () => reject(new Error(url)), { once: true });
        }
        return;
      }

      const element = document.createElement(tagName);
      element.dataset.reviewGalleryAsset = url;
      if (tagName === 'link') {
        element.rel = 'stylesheet';
        element.href = url;
      } else {
        element.src = url;
        element.defer = true;
      }
      element.addEventListener(
        'load',
        () => {
          element.dataset.loaded = 'true';
          resolve();
        },
        { once: true }
      );
      element.addEventListener('error', () => reject(new Error(url)), { once: true });
      document.head.appendChild(element);
    });
  }

  /**
   * Fetches the lightbox CSS + JS on first use and waits for the element to be
   * defined. A plain tag injection is used rather than `import()` so no CORS
   * headers are required from the Shopify CDN.
   * @param {string} scriptUrl
   * @param {string} styleUrl
   * @returns {Promise<HTMLElement>}
   */
  async function ensureLightbox(scriptUrl, styleUrl) {
    if (!lightboxAssets) {
      lightboxAssets = Promise.all([loadResource('link', styleUrl), loadResource('script', scriptUrl)]);
    }
    await lightboxAssets;
    await customElements.whenDefined('m-review-lightbox');

    if (!lightboxElement || !lightboxElement.isConnected) {
      lightboxElement = document.createElement('m-review-lightbox');
      document.body.appendChild(lightboxElement);
    }
    return lightboxElement;
  }

  class ReviewViewer extends HTMLElement {
    constructor() {
      super();
      this.strings = {};
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      if (this.dataset.lightbox !== 'true') return;
      this.strings = this.readStrings();
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    /**
     * Reads merchant-translated UI strings emitted by the section.
     * @returns {Record<string, string>}
     */
    readStrings() {
      const node = this.querySelector('[data-rg-strings]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return {};
      }
    }

    /**
     * Every card that can open the lightbox, clones included.
     * @returns {HTMLElement[]}
     */
    get triggers() {
      return Array.from(this.querySelectorAll('.m-review-gallery__media[data-full-src]'));
    }

    /**
     * The first card carrying an index. A marquee clone repeats its original's
     * index and always sits later in the DOM, so the first match is the
     * original - which is what focus must return to on close.
     * @param {number} index
     * @returns {HTMLElement|null}
     */
    originalFor(index) {
      return this.triggers.find((media) => Number(media.dataset.index) === index) || null;
    }

    /**
     * Builds the lightbox data set, de-duplicated by index so repeated clones
     * contribute nothing.
     * @returns {Array<{src: string, width: number, height: number, alt: string, thumb: string}>}
     */
    collectItems() {
      const byIndex = new Map();

      this.triggers.forEach((media) => {
        const index = Number(media.dataset.index);
        if (!Number.isInteger(index) || byIndex.has(index)) return;

        const image = media.querySelector('.m-review-gallery__image');
        byIndex.set(index, {
          src: media.dataset.fullSrc,
          width: Number(media.dataset.fullWidth) || 0,
          height: Number(media.dataset.fullHeight) || 0,
          alt: image ? image.alt : '',
          thumb: image ? image.currentSrc || image.src : '',
        });
      });

      return Array.from(byIndex.keys())
        .sort((a, b) => a - b)
        .map((index) => byIndex.get(index));
    }

    onClick(event) {
      const media = event.target.closest('button.m-review-gallery__media');
      if (!media || !this.contains(media)) return;

      const index = Number(media.dataset.index) || 0;
      // Resolve clones back to their original so focus never returns into
      // `aria-hidden` content when the lightbox closes.
      this.open(index, this.originalFor(index) || media);
    }

    /**
     * Lazily loads the lightbox bundle, then opens it on the requested review.
     * @param {number} index
     * @param {HTMLElement} trigger
     */
    async open(index, trigger) {
      this.emit('rg:viewer-open');
      try {
        const lightbox = await ensureLightbox(this.dataset.lightboxJs, this.dataset.lightboxCss);
        lightbox.open({ items: this.collectItems(), index, trigger, strings: this.strings });
        lightbox.addEventListener('rg:lightbox-close', () => this.emit('rg:viewer-close'), { once: true });
      } catch (error) {
        this.emit('rg:viewer-close');
      }
    }

    /** @param {string} name */
    emit(name) {
      this.dispatchEvent(new CustomEvent(name, { bubbles: true }));
    }
  }

  customElements.define('m-review-viewer', ReviewViewer);
}
